import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import PlusIcon from '@jwp/ott-theme/assets/icons/plus_blue.svg?react';
import CrossIcon from '@jwp/ott-theme/assets/icons/cross_blue.svg?react';
import ReelIcon from '@jwp/ott-theme/assets/icons/reel.svg?react';
import VideoIcon from '@jwp/ott-theme/assets/icons/video.svg?react';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

import { useTheme } from '../../../../../ThemeContext';
import Icon from '../Icon/Icon';

import styles from './UploadButton.module.scss';

const UploadButton = () => {
  const { t } = useTranslation('upload');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isUploadEnabled, isReelUploadEnabled, isVideoUploadEnabled } = useAppConfig();

  const toggleMenu = () => setIsOpen((prev) => !prev);
  const { universalStyles } = useTheme();
  const resetUploadStore = useUploadStore((state) => state.reset);
  const canUploadReel = isUploadEnabled && isReelUploadEnabled;
  const canUploadVideo = isUploadEnabled && isVideoUploadEnabled;
  const availableActions = [
    canUploadReel ? { action: 'reel' as const, label: t('reel'), icon: ReelIcon } : null,
    canUploadVideo ? { action: 'video' as const, label: t('video'), icon: VideoIcon } : null,
  ].filter(Boolean) as Array<{ action: 'reel' | 'video'; label: string; icon: React.FC<React.SVGProps<SVGSVGElement>> }>;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAction = (action: 'reel' | 'video') => {
    if (action === 'reel') {
      resetUploadStore();
      useUploadStore.getState().setInitiated(true);
      navigate(createURLFromLocation(location, { u: 'upload-reel' }));
    } else if (action === 'video') {
      resetUploadStore();
      useUploadStore.getState().setInitiated(true);
      navigate(createURLFromLocation(location, { u: 'upload-video' }));
    }
    setIsOpen(false);
  };

  return availableActions.length === 0 ? null : (
    <div className={styles.uploadContainer} ref={containerRef}>
      <div className={classNames(styles.menu, { [styles.open]: isOpen })}>
        {availableActions.map((item, index) => (
          <React.Fragment key={item.action}>
            <button className={styles.menuItem} onClick={() => handleAction(item.action)} style={universalStyles.primaryTextColor}>
              <Icon icon={item.icon} />
              {item.label}
            </button>
            {index < availableActions.length - 1 && <div className={styles.divider} />}
          </React.Fragment>
        ))}
      </div>
      <button className={styles.fab} onClick={toggleMenu} aria-label="Upload content" aria-expanded={isOpen}>
        <span className={styles.iconWrapper}>{isOpen ? <Icon icon={CrossIcon} /> : <Icon icon={PlusIcon} />}</span>
      </button>
    </div>
  );
};

export default UploadButton;
